/**
 * User Authentication Modal
 * Handles user sign up and sign in for custom safari builder
 */

import React, { useState, useEffect } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useGoogleLogin } from '@react-oauth/google';
import { userAuthService, User } from '../../services/userAuthService';
import { useTheme } from '../../contexts/ThemeContext';

interface UserAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  mode?: 'signin' | 'signup';
}

const UserAuthModal: React.FC<UserAuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode: initialMode = 'signin'
}) => {
  const { actualTheme } = useTheme();
  // Initialize mode from prop, but also update it if initialMode changes
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Sync mode state with initialMode prop when it changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setError(null);
      setSuccess(null);
    }
  }, [initialMode, isOpen]);

  const googleLoginHandler = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        setError(null);
        const response = await userAuthService.googleLogin(tokenResponse.access_token, 'accessToken');
        if (response.success && response.user) {
          setSuccess('Signed in with Google!');
          onSuccess(response.user);
          setTimeout(() => {
            onClose();
            resetForm();
          }, 1000);
        } else {
          setError(response.message || 'Google Login failed');
        }
      } catch (err: any) {
        setError(err.message || 'Google Login failed');
      } finally {
        setLoading(false);
      }
    },
    onError: () => setError('Google Login was cancelled or failed')
  });

  const handleGoogleSignIn = () => {
    setError(null);
    googleLoginHandler();
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (mode === 'signup') {
        // Validation
        if (!name || !email || !phone || !password) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }

        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        if (confirmPassword !== undefined && password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        // Phone validation (basic)
        const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
        if (!phoneRegex.test(phone)) {
          setError('Please enter a valid phone number');
          setLoading(false);
          return;
        }

        const response = await userAuthService.signUp(
          email.trim(),
          password,
          name.trim(),
          phone.trim()
        );

        if (!response.success) {
          setError(response.message || 'Registration failed');
        } else if (response.user) {
          setSuccess('Account created successfully!');
          onSuccess(response.user);
          // Small delay before close to show success
          setTimeout(() => {
            onClose();
            resetForm();
          }, 1500);
        } else {
          setError('Account creation failed. Please try again.');
        }
      } else {
        // Sign in
        if (!email || !password) {
          setError('Please enter both email and password');
          setLoading(false);
          return;
        }

        const response = await userAuthService.signIn(email.trim(), password);

        if (!response.success) {
          setError(response.message || 'Login failed');
        } else if (response.user) {
          setSuccess('Signed in successfully!');
          onSuccess(response.user);
          // Small delay before close to show success
          setTimeout(() => {
            onClose();
            resetForm();
          }, 1000);
        } else {
          setError('Sign in failed. Please try again.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" style={{ pointerEvents: 'auto' }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      />

      {/* Modal / Container */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className={`relative w-full max-w-md rounded-2xl shadow-2xl ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
          }`}>
          {/* Close button inside modal */}
          <button
            onClick={onClose}
            className={`absolute right-4 top-4 p-2 rounded-lg transition-colors z-10 ${actualTheme === 'dark'
              ? 'text-slate-400 hover:bg-slate-700 hover:text-white'
              : 'text-neutral-600 hover:bg-neutral-100 hover:text-primary-800'
              }`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Header */}
          <div className={`p-6 border-b ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
            }`}>
            <h2 className={`text-2xl font-bold transition-all ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
              }`}>
              {mode === 'signup' ? 'Create Account' : 'Sign In'}
            </h2>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
            {error && (
              <div className={`p-3 rounded-lg flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300 ${actualTheme === 'dark' ? 'bg-red-900/30 border border-red-700/50' : 'bg-red-50 border border-red-200'
                }`}>
                <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                <p className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-red-300' : 'text-red-700'
                  }`}>
                  {error}
                </p>
              </div>
            )}

            {success && (
              <div className={`p-3 rounded-lg flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300 ${actualTheme === 'dark' ? 'bg-green-900/30 border border-green-700/50' : 'bg-green-50 border border-green-200'
                }`}>
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <p className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-green-300' : 'text-green-700'
                  }`}>
                  {success}
                </p>
              </div>
            )}

            {mode === 'signup' && (
              <div className="space-y-1 animate-in fade-in slide-in-from-left-4 duration-300">
                <label className={`block text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                  }`}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:border-primary-500'
                    : 'bg-white border-neutral-300 text-neutral-900 focus:border-primary-600'
                    } outline-none ring-primary-500/20 focus:ring-4`}
                  placeholder="John Doe"
                />
              </div>
            )}

            <div className="space-y-1">
              <label className={`block text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                }`}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`w-full px-4 py-2 rounded-lg border transition-all ${actualTheme === 'dark'
                  ? 'bg-slate-700 border-slate-600 text-white focus:border-primary-500'
                  : 'bg-white border-neutral-300 text-neutral-900 focus:border-primary-600'
                  } outline-none ring-primary-500/20 focus:ring-4`}
                placeholder="your@email.com"
              />
            </div>

            {mode === 'signup' && (
              <div className="space-y-1 animate-in fade-in slide-in-from-left-4 duration-500">
                <label className={`block text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                  }`}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:border-primary-500'
                    : 'bg-white border-neutral-300 text-neutral-900 focus:border-primary-600'
                    } outline-none ring-primary-500/20 focus:ring-4`}
                  placeholder="+254 708 560 665"
                />
                <p className={`text-[10px] mt-1 italic ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                  }`}>
                  Helps us automate your safari itinerary follow-ups
                </p>
              </div>
            )}

            <div className="space-y-1">
              <label className={`block text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                }`}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`w-full px-4 py-2 pr-10 rounded-lg border transition-all ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:border-primary-500'
                    : 'bg-white border-neutral-300 text-neutral-900 focus:border-primary-600'
                    } outline-none ring-primary-500/20 focus:ring-4`}
                  placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors ${actualTheme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-neutral-500 hover:text-neutral-800'
                    }`}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {mode === 'signup' && (
              <div className="space-y-1 animate-in fade-in slide-in-from-left-4 duration-700">
                <label className={`block text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                  }`}>
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:border-primary-500'
                    : 'bg-white border-neutral-300 text-neutral-900 focus:border-primary-600'
                    } outline-none ring-primary-500/20 focus:ring-4`}
                  placeholder="Repeat your password"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-xl font-bold text-lg shadow-lg shadow-primary-500/20 transform active:scale-[0.98] transition-all flex items-center justify-center gap-2 ${actualTheme === 'dark'
                ? 'bg-primary-600 hover:bg-primary-500 text-white'
                : 'bg-primary-800 hover:bg-primary-700 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading && (
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {loading ? 'Processing...' : mode === 'signup' ? 'Create Account' : 'Sign In'}
            </button>

            {/* Google Separator */}
            <div className="relative py-4">
              <div className={`absolute inset-0 flex items-center ${actualTheme === 'dark' ? 'text-slate-700' : 'text-neutral-200'}`}>
                <span className="w-full border-t border-current"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-widest font-bold">
                <span className={`px-4 ${actualTheme === 'dark' ? 'bg-slate-800 text-slate-500' : 'bg-white text-neutral-400'}`}>
                  Or continue with
                </span>
              </div>
            </div>

            {/* Google Button */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className={`w-full py-3 rounded-xl font-semibold border-2 flex items-center justify-center gap-3 transition-all hover:shadow-md active:scale-[0.98] ${actualTheme === 'dark'
                ? 'bg-slate-700/50 border-slate-600/50 text-white hover:bg-slate-600'
                : 'bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => {
                  setMode(mode === 'signin' ? 'signup' : 'signin');
                  setError(null);
                  setSuccess(null);
                }}
                className={`text-sm font-semibold transition-colors ${actualTheme === 'dark'
                  ? 'text-primary-400 hover:text-primary-300'
                  : 'text-primary-800 hover:text-primary-700 underline underline-offset-4'
                  }`}
              >
                {mode === 'signin'
                  ? "Don't have an account? Sign up"
                  : 'Already have an account? Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserAuthModal;
